// The single client tax-basis calculation, used by the tax return model
// (backend-tax-return.jsx) and the tax return page (closing.jsx) so the page
// never shows a different tax than the model. Mirrors
// functions/yearClosing/yearClosingAutomaticEntries.ts (buildClosingTaxCalculation)
// and functions/yearClosing/taxBasisNotCalculatedInputs.ts.
(function registerTaxBasisCalculation(global: any) {
  const list = (value: any): any[] => Array.isArray(value) ? value : [];
  const number = (value: any): number => Number.isFinite(Number(value)) ? Number(value) : 0;

  // `taxRate` is supplied only for ordinary Norwegian company tax. Skatte-ABC
  // U-2-4.7: eligible prior loss cannot be deferred, so the whole available
  // loss is applied and `aaretsAnv` is ignored. Without a corporate rate (ENK,
  // review-required regimes) the requested amount caps the use.
  const calculate = (taxBasis: any, resultBeforeTax: any, taxRate: any) => {
    const permanent = list(taxBasis?.permanentDifferences);
    const temporary = list(taxBasis?.temporaryDifferences);
    const loss = taxBasis?.lossCarryforward || {};
    const permanentDifference = permanent.reduce((sum, entry) => sum + (entry?.kind === 'tillegg' ? number(entry.amount) : entry?.kind === 'fradrag' ? -number(entry.amount) : 0), 0);
    const temporaryDifferenceChange = temporary.reduce((sum, entry) => sum + ((number(entry?.rmAar) - number(entry?.smAar)) - (number(entry?.rmFjor) - number(entry?.smFjor))), 0);
    const temporaryDifference = temporary.reduce((sum, entry) => sum + (number(entry?.rmAar) - number(entry?.smAar)), 0);
    const baseRaw = number(resultBeforeTax) + permanentDifference - temporaryDifferenceChange;
    const lossCarry = baseRaw < 0 ? -baseRaw : 0;
    const taxableBeforeLoss = Math.max(0, baseRaw);
    const priorLossCarryforward = Math.max(0, number(loss.fremfortTidl));
    const requestedLossApplied = Math.max(0, number(loss.aaretsAnv));
    const hasRate = taxRate !== null && taxRate !== undefined && Number.isFinite(Number(taxRate));
    const lossApplied = hasRate
      ? Math.min(taxableBeforeLoss, priorLossCarryforward)
      : Math.min(taxableBeforeLoss, priorLossCarryforward, requestedLossApplied);
    const taxable = Math.max(0, taxableBeforeLoss - lossApplied);
    // An ENK has no company rate: the tax is assessed personally, so the model
    // reports null rather than a misleading 0.
    const taxAmount = hasRate ? Math.round(taxable * Number(taxRate)) : null;
    const fremfortInntekt = Math.max(0, lossCarry + priorLossCarryforward - lossApplied);
    const sumDTA = temporaryDifference - fremfortInntekt;
    const ikkeBokfort = taxBasis?.deferredTaxAssetOverride != null ? number(taxBasis.deferredTaxAssetOverride) : Math.max(0, fremfortInntekt - temporaryDifference);
    const grunnlagDTA = sumDTA + ikkeBokfort;
    return {
      permanentDifference, temporaryDifferenceChange, temporaryDifference, taxableBeforeLoss,
      priorLossCarryforward, requestedLossApplied, lossApplied, lossUseCalculated: hasRate, taxable, taxAmount,
      lossCarry, fremfortInntekt, sumDTA, ikkeBokfort, grunnlagDTA,
      utsattSkatt: hasRate ? Math.round(grunnlagDTA * Number(taxRate)) : null,
    };
  };

  // Inputs Plumme stores but does not calculate. No sourced rule exists in the
  // accounting guardrails for these, so entering one makes the tax review-required.
  const notCalculatedSpecs: Record<string, string> = {
    Konsernbidrag: 'groupContribution',
    'Forskning og utvikling (skattefunn)': 'skattefunn',
    'Fradrag for skatt betalt i utlandet': 'foreignTaxCredit',
    'Fradrag for skattelegging av utbytte mv. fra utenlandsk datterselskap': 'foreignSubsidiaryTaxCredit',
    'Tilbakeført utenlandsk skatt senere år': 'foreignTaxReversal',
  };
  const enteredField = (value: any) => {
    const text = String(value ?? '').trim();
    if (!text || /^(nei|no)$/i.test(text)) return false;
    const amount = Number(text.replace(/\s/g, '').replace(',', '.'));
    return !(Number.isFinite(amount) && amount === 0);
  };
  const specHasValues = (spec: any) => Boolean(spec) && (
    Object.values(spec.fields || {}).some(enteredField)
    || Object.values(spec.tables || {}).some((rows: any) => list(rows).some((row: any) => list(row).some((cell: any) => String(cell ?? '').trim())))
    || Object.values(spec.schemes || {}).some((rows: any) => list(rows).length > 0)
  );
  const notCalculatedInputs = (process: any): string[] => {
    const basis = process?.taxBasis || {};
    const specs = new Set(list(basis.extraSpecs).map(String));
    const values = basis.extraSpecValues || {};
    const inputs = Object.entries(notCalculatedSpecs).filter(([title]) => specs.has(title) && specHasValues(values[title])).map(([, id]) => id);
    const loss = basis.lossCarryforward || {};
    if ([loss.underhand, loss.restUnderhand].some((value) => value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) !== 0)) inputs.push('debtForgiveness');
    if (list(process?.taxAssets?.items).some((item: any) => item?.source !== 'fixed-assets')) inputs.push('manualTaxAssets');
    return inputs;
  };
  const isNotCalculatedSpec = (title: any) => Object.prototype.hasOwnProperty.call(notCalculatedSpecs, String(title));

  global.PlummeTaxBasisCalculation = { calculate, notCalculatedInputs, isNotCalculatedSpec };
})(window);
